const SCROLLBAR_LIGHT = `
  html {
    scrollbar-width: thin;
    scrollbar-color: rgba(100, 116, 139, 0.55) transparent;
  }
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(100, 116, 139, 0.45);
    border-radius: 999px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: rgba(79, 70, 229, 0.55);
  }
`;

const BODY_BASE = `
  html {
    color-scheme: light only;
  }
  html, body {
    margin: 0;
    padding: 16px;
    font-family: "Poppins", system-ui, sans-serif;
    font-size: 14px;
    line-height: 1.55;
    word-wrap: break-word;
    overflow-wrap: anywhere;
    color-scheme: light only !important;
  }
  img, video, svg { max-width: 100%; height: auto; }
  img:not([src]), img[src=""], img[src^="cid:" i], img[src^="CID:"] { display: none !important; }
  table { max-width: 100%; }
  pre { white-space: pre-wrap; }
`;

const LIGHT_STYLES = `
  ${BODY_BASE}
  ${SCROLLBAR_LIGHT}
  html, body {
    background: #ffffff;
    color: #0f172a;
  }
  a { color: #4f46e5; }
  blockquote {
    border-left: 3px solid #e2e8f0;
    padding-left: 12px;
    color: #64748b;
  }
`;

/** Empêche Windows / le navigateur d’appliquer le thème sombre du mail reçu. */
function neutralizeWindowsDarkScheme(html: string): string {
  return html
    .replace(/prefers-color-scheme\s*:\s*dark/gi, "max-width: 0px")
    .replace(/color-scheme\s*:\s*dark(?:\s+only)?/gi, "color-scheme: light");
}

function injectStylesIntoHtml(html: string, styles: string): string {
  const headBits =
    '<meta name="color-scheme" content="light only">' +
    `<style data-mail-theme>${styles}</style>`;

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${headBits}</head>`);
  }
  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${headBits}`);
  }
  if (/<html[\s>]/i.test(html)) {
    return html.replace(/<html([^>]*)>/i, `<html$1><head>${headBits}</head>`);
  }

  return `<!DOCTYPE html><html lang="fr" style="color-scheme:light"><head><meta charset="utf-8">${headBits}</head><body>${html}</body></html>`;
}

function shouldOpenLinkInNewTab(href: string): boolean {
  const h = href.trim().toLowerCase();
  if (!h || h.startsWith("#")) return false;
  if (h.startsWith("mailto:") || h.startsWith("tel:") || h.startsWith("javascript:")) {
    return false;
  }
  return true;
}

/** Nettoie le HTML reçu (scripts, liens dangereux) et ouvre les liens en nouvel onglet. */
function sanitizeMailBodyHtml(html: string): string {
  const trimmed = html.trim();
  if (!trimmed || typeof DOMParser === "undefined") return neutralizeWindowsDarkScheme(trimmed);

  const hadDoctype = /^\s*<!DOCTYPE/i.test(trimmed);
  const doc = new DOMParser().parseFromString(trimmed, "text/html");

  doc.querySelectorAll("script, iframe, object, embed").forEach((el) => el.remove());

  doc.documentElement.style.colorScheme = "light";
  doc.documentElement.setAttribute("style", mergeColorSchemeLight(doc.documentElement.getAttribute("style")));
  if (doc.body) {
    doc.body.style.colorScheme = "light";
  }

  doc.querySelectorAll('meta[name="color-scheme" i]').forEach((meta) => {
    meta.setAttribute("content", "light only");
  });

  doc.querySelectorAll("style").forEach((style) => {
    style.textContent = neutralizeWindowsDarkScheme(style.textContent ?? "");
  });

  doc.querySelectorAll("img").forEach((img) => {
    if (!img.hasAttribute("loading")) {
      img.setAttribute("loading", "lazy");
    }
    if (!img.hasAttribute("decoding")) {
      img.setAttribute("decoding", "async");
    }
  });

  doc.querySelectorAll("[href], [src]").forEach((el) => {
    const href = (el.getAttribute("href") ?? el.getAttribute("src") ?? "").trim().toLowerCase();
    if (href.startsWith("javascript:")) {
      el.removeAttribute("href");
      el.removeAttribute("src");
    }
  });

  doc.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    if (!shouldOpenLinkInNewTab(href)) return;

    // Secours si l’interception parent échoue — l’UI force aussi l’ouverture hors PWA.
    anchor.setAttribute("target", "_blank");
    const relParts = new Set((anchor.getAttribute("rel") ?? "").split(/\s+/).filter(Boolean));
    relParts.add("noopener");
    relParts.add("noreferrer");
    anchor.setAttribute("rel", [...relParts].join(" "));
  });

  doc.querySelectorAll("area[href]").forEach((area) => {
    const href = area.getAttribute("href") ?? "";
    if (!shouldOpenLinkInNewTab(href)) return;
    area.setAttribute("target", "_blank");
    const relParts = new Set((area.getAttribute("rel") ?? "").split(/\s+/).filter(Boolean));
    relParts.add("noopener");
    relParts.add("noreferrer");
    area.setAttribute("rel", [...relParts].join(" "));
  });

  const serialized = neutralizeWindowsDarkScheme(doc.documentElement.outerHTML);
  return hadDoctype ? `<!DOCTYPE html>\n${serialized}` : serialized;
}

function mergeColorSchemeLight(existing: string | null): string {
  const base = (existing ?? "").trim().replace(/;+\s*$/, "");
  if (!base) return "color-scheme: light";
  if (/color-scheme\s*:/i.test(base)) {
    return base.replace(/color-scheme\s*:\s*[^;]+/i, "color-scheme: light");
  }
  return `${base};color-scheme: light`;
}

/** Document iframe de lecture — toujours clair, indépendant de Windows et du thème Mail. */
export function buildMailBodyDocument(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return "";

  return injectStylesIntoHtml(sanitizeMailBodyHtml(trimmed), LIGHT_STYLES);
}
